import { useMemo } from 'react';

/**
 * @typedef {object} CombinedCostInfo
 * @property {number} totalPostageCost - The total postage cost including walleted providers
 * @property {number} totalPurchaseAmount - The total purchase amount including walleted providers
 * @property {number} nonWalletTotalPostageCost - The total postage cost excluding walleted providers
 * @property {number} nonWalletPurchaseAmount - The total purchase amount excluding walleted providers
 * @property {number} totalWalletPostageCost - The total cost from walleted providers
 * @property {number} totalWalletPurchaseAmount - The total purchase amount from walleted providers
 */

/**
 * Combines cost information from providers, insurances, and walleted provider groups.
 * @param {Array<{ subTotal: number, purchaseAmount: number }>} providers - List of providers with their costs.
 * @param {Array<{ subTotal: number, purchaseAmount: number, walletId?: string }>} insurances - List of insurance costs.
 * @param {Array<Array<{ subTotal: number, purchaseAmount: number }>>} walletedProviderGroups - Grouped walleted providers.
 * @returns {CombinedCostInfo} Combined cost information.
 */

export const useCombinedCostInfo = (providers, insurances, walletedProviderGroups) => {
  return useMemo(() => {
    const totalWalletPostageCost = Number(
      walletedProviderGroups.reduce((acc, costs) => acc + costs.reduce((acc2, cost) => acc2 + (Number(cost?.subTotal) || 0), 0), 0),
    );
    const totalWalletPurchaseAmount = Number(
      walletedProviderGroups.reduce((acc, costs) => acc + (Number(costs?.[0]?.purchaseAmount) || 0), 0),
    );

    const nonWalletedInsuranceCosts = insurances.filter((insurance) => !insurance.walletId);
    const allProviders = [...providers, ...nonWalletedInsuranceCosts];

    const nonWalletTotalPostageCost = [...providers, ...walletedProviderGroups].reduce(
      (acc, prov) => acc + (Number(prov?.subTotal) || 0),
      0,
    );
    const nonWalletPurchaseAmount = allProviders.reduce((acc, prov) => acc + (Number(prov?.purchaseAmount) || 0), 0);

    const totalPostageCost = nonWalletTotalPostageCost + totalWalletPostageCost;
    const totalPurchaseAmount = nonWalletPurchaseAmount + totalWalletPurchaseAmount;

    return {
      totalPostageCost,
      totalPurchaseAmount,
      nonWalletTotalPostageCost,
      nonWalletPurchaseAmount,
      totalWalletPostageCost,
      totalWalletPurchaseAmount,
    };
  }, [providers, insurances, walletedProviderGroups]);
};
